import ReactDOM from 'react-dom';
import { AssetShareComment, AssetShareImageInfo, AssetShareLinkInfo } from '../../../api/assetShare';
import { formatDate } from '../../../utils/formatDate';
import { tosShareImage } from '../../../utils/tosImage';
import { useIsMobile } from '../../../utils/useIsMobile';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface AssetShareHistoryModalProps {
  open: boolean;
  history: AssetShareLinkInfo[];
  historyLoading: boolean;
  historyError: string;
  selectedLink: AssetShareLinkInfo | null;
  comments: AssetShareComment[];
  commentsLoading: boolean;
  commentsError: string;
  imageMap: Record<string, AssetShareImageInfo>;
  t: Translate;
  onClose: () => void;
  onBack: () => void;
  onLoadComments: (link: AssetShareLinkInfo) => void;
  onCopyLink: (code: string) => void;
}

interface ImageGroup {
  imageId: string;
  info: AssetShareImageInfo;
  comments: AssetShareComment[];
}

interface CharacterCommentGroup {
  characterName: string;
  casting: ImageGroup[];
  stylingStates: Record<string, ImageGroup[]>;
}

function buildCharacterGroups(
  comments: AssetShareComment[],
  imageMap: Record<string, AssetShareImageInfo>,
): Record<string, CharacterCommentGroup> {
  const commentsByImage: Record<string, AssetShareComment[]> = {};
  for (const comment of comments) {
    const key = comment.assetImageId || 'unknown';
    if (!commentsByImage[key]) commentsByImage[key] = [];
    commentsByImage[key].push(comment);
  }

  const charGroups: Record<string, CharacterCommentGroup> = {};

  for (const [imageId, imgComments] of Object.entries(commentsByImage)) {
    const info = imageMap[imageId];
    if (!info) continue;

    if (!charGroups[info.characterName]) {
      charGroups[info.characterName] = {
        characterName: info.characterName,
        casting: [],
        stylingStates: {},
      };
    }
    const group = charGroups[info.characterName];
    const imgGroup: ImageGroup = { imageId, info, comments: imgComments };

    if (info.category === 'casting') {
      group.casting.push(imgGroup);
    } else {
      const state = info.state || '';
      if (!group.stylingStates[state]) group.stylingStates[state] = [];
      group.stylingStates[state].push(imgGroup);
    }
  }

  for (const [imageId, info] of Object.entries(imageMap)) {
    if (commentsByImage[imageId]) continue;
    if (!charGroups[info.characterName]) {
      charGroups[info.characterName] = {
        characterName: info.characterName,
        casting: [],
        stylingStates: {},
      };
    }
    const group = charGroups[info.characterName];
    const imgGroup: ImageGroup = { imageId, info, comments: [] };

    if (info.category === 'casting') {
      group.casting.push(imgGroup);
    } else {
      const state = info.state || '';
      if (!group.stylingStates[state]) group.stylingStates[state] = [];
      group.stylingStates[state].push(imgGroup);
    }
  }

  return charGroups;
}

function RoleBadge({ role }: { role: string }) {
  if (!role) return null;
  const label = role === 'primary' ? '主' : '备选';
  return (
    <span
      style={{
        position: 'absolute',
        top: 2,
        left: 2,
        background: role === 'primary' ? 'rgba(139,92,246,0.85)' : 'rgba(107,114,128,0.8)',
        color: '#fff',
        fontSize: '0.6rem',
        lineHeight: 1,
        padding: '1px 3px',
        borderRadius: 2,
      }}
    >
      {label}
    </span>
  );
}

function ImageCommentRow({ imgGroup }: { imgGroup: ImageGroup }) {
  const { info, comments: imgComments } = imgGroup;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', padding: '0.4rem 0' }}>
      <div style={{ position: 'relative', flexShrink: 0, width: 48, height: 62 }}>
        <img
          src={tosShareImage(info.imageUrl, 48)}
          alt=""
          style={{ width: 48, height: 62, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }}
        />
        <RoleBadge role={info.role} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {imgComments.length === 0 ? (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', paddingTop: '0.3rem' }}>
            {'暂无评论'}
          </div>
        ) : (
          imgComments.map((comment, idx) => (
            <div
              key={comment.id}
              style={{
                padding: '0.3rem 0',
                borderTop: idx > 0 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{ fontSize: '0.83rem', color: 'var(--text-primary)' }}>{comment.content}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                {formatDate(comment.createdAt)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SectionDot({ color }: { color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        marginRight: 6,
        verticalAlign: 'middle',
      }}
    />
  );
}

function CharacterGroupView({ group }: { group: CharacterCommentGroup }) {
  const hasCasting = group.casting.length > 0;
  const stylingStates = Object.entries(group.stylingStates);
  const hasAnyContent = hasCasting || stylingStates.length > 0;

  if (!hasAnyContent) {
    return (
      <div style={{ marginBottom: '0.75rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderLeft: '3px solid #8b5cf6',
            paddingLeft: '0.5rem',
            marginBottom: '0.4rem',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
            {group.characterName}
          </span>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', paddingLeft: '0.75rem' }}>
          {'暂无评论'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          borderLeft: '3px solid #8b5cf6',
          paddingLeft: '0.5rem',
          marginBottom: '0.4rem',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
          {group.characterName}
        </span>
      </div>

      {hasCasting && (
        <div style={{ paddingLeft: '0.75rem', marginBottom: '0.3rem' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>
            <SectionDot color="#f59e0b" />
            {'人脸'}
          </div>
          {group.casting.map(imgGroup => (
            <ImageCommentRow key={imgGroup.imageId} imgGroup={imgGroup} />
          ))}
        </div>
      )}

      {stylingStates.map(([state, images]) => (
        <div key={state} style={{ paddingLeft: '0.75rem', marginBottom: '0.3rem' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>
            <SectionDot color="#8b5cf6" />
            {state || '默认'}
          </div>
          {images.map(imgGroup => (
            <ImageCommentRow key={imgGroup.imageId} imgGroup={imgGroup} />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function AssetShareHistoryModal({
  open,
  history,
  historyLoading,
  historyError,
  selectedLink,
  comments,
  commentsLoading,
  commentsError,
  imageMap,
  t,
  onClose,
  onBack,
  onLoadComments,
  onCopyLink,
}: AssetShareHistoryModalProps) {
  const isMobile = useIsMobile();

  if (!open) return null;

  const charGroups = selectedLink ? buildCharacterGroups(comments, imageMap) : {};

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: '1.5rem', width: isMobile ? 'calc(100vw - 2rem)' : '420px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
      >
        {!selectedLink ? (
          <>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>{t('assetShare.historyTitle')}</h3>
            {historyError && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem' }}>{historyError}</p>
            )}
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <span className="loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></span>
              </div>
            ) : history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                {t('assetShare.noShareRecords')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {history.map(link => {
                  const expired = new Date(link.expiresAt) < new Date();
                  return (
                    <button
                      key={link.id}
                      onClick={() => onLoadComments(link)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.6rem 0.75rem', background: 'var(--background)', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)', cursor: 'pointer', textAlign: 'left', width: '100%',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{link.label}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                          {formatDate(link.createdAt)}
                          {expired && <span style={{ color: 'var(--danger)', marginLeft: '0.5rem' }}>{t('assetShare.expired')}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.8rem', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onCopyLink(link.code);
                          }}
                          style={{
                            background: 'none', border: '1px solid var(--border)', borderRadius: '4px',
                            cursor: 'pointer', padding: '2px 8px', fontSize: '0.75rem', color: 'var(--text-secondary)',
                          }}
                        >
                          {t('assetShare.copyLink')}
                        </button>
                        {link.commentCount > 0 && <span>{t('assetShare.nComments', { count: link.commentCount })}</span>}
                        <span style={{ fontSize: '1rem' }}>{'›'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <button
                onClick={onBack}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', padding: '0', color: 'var(--text-primary)' }}
              >
                {t('assetShare.back')}
              </button>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>{selectedLink.label}</h3>
            </div>
            {commentsError && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem' }}>{commentsError}</p>
            )}
            {commentsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <span className="loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }}></span>
              </div>
            ) : Object.keys(charGroups).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                {t('assetShare.noComments')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {Object.values(charGroups).map(group => (
                  <CharacterGroupView key={group.characterName} group={group} />
                ))}
              </div>
            )}
          </>
        )}
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={onClose}>{t('close', { ns: 'common' })}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
